import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import type { AITaskSetting } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, ListRow, NakedUl, SwitchField, useForm } from '@frontend/design-system';
import { UnauthorizedAccess } from '../unauthorized-access';

interface AutoAssignTaskSettingsProps {
  values?: Pick<AITaskSetting, 'autoAssignTaskEnabled'>;
  mixedValues?: Pick<AITaskSetting, 'autoAssignTaskEnabled'>;
  isAuthorized?: boolean;
  isLoading?: boolean;
  onToggle?: (values?: Pick<AITaskSetting, 'autoAssignTaskEnabled'>) => void;
}

export const AutoAssignTaskSettingsForm: FC<AutoAssignTaskSettingsProps> = ({
  values,
  mixedValues,
  isAuthorized,
  isLoading,
  onToggle,
}) => {
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });

  const {
    formProps,
    values: formValues,
    getFieldProps,
    seedValues,
  } = useForm({
    fields: {
      autoAssignTaskEnabled: {
        type: 'switch',
        value: values?.autoAssignTaskEnabled || false,
      },
    },
  });

  useEffect(() => {
    // Check if values have changed and update form accordingly
    if ((values?.autoAssignTaskEnabled ?? false) !== formValues.autoAssignTaskEnabled) {
      seedValues({
        autoAssignTaskEnabled: values?.autoAssignTaskEnabled || false,
      });
      return;
    }
  }, [values?.autoAssignTaskEnabled]);

  if (!isAuthorized) {
    return <UnauthorizedAccess />;
  }

  return (
    <form css={defaultStyles.formContainer} {...formProps}>
      <ContentLoader show={isLoading} />
      <NakedUl>
        <ListRow css={defaultStyles.listRow} data-testid='auto-assign-task-setting-row'>
          <ListRow.Content>
            <ListRow.Content.Title>{t('Auto-assign Call Intelligence Tasks')}</ListRow.Content.Title>
            <ListRow.Content.Subtitle>
              {t('Enable Call Intelligence to auto-assign AI tasks to the Office User who handled the call.')}
            </ListRow.Content.Subtitle>
          </ListRow.Content>
          <ListRow.Trail>
            <SwitchField
              label=''
              {...getFieldProps('autoAssignTaskEnabled')}
              indeterminate={mixedValues?.autoAssignTaskEnabled}
              onChange={(e) => {
                getFieldProps('autoAssignTaskEnabled').onChange(e);
                onToggle?.({
                  autoAssignTaskEnabled: (e as { value: boolean }).value,
                });
              }}
            />
          </ListRow.Trail>
        </ListRow>
      </NakedUl>
    </form>
  );
};

const defaultStyles = {
  formContainer: css`
    display: flex;
    flex-direction: column;
    position: relative;
  `,
  listRow: css`
    padding: 8px 0px;
    border-bottom: none;
  `,
};
